import React, { useState, useEffect } from 'react';
import {
  HeadphonesIcon, RefreshCw, Archive, Inbox, ArrowLeft, CheckCircle,
  MessageSquare, Send
} from 'lucide-react';
import {
  getAllTickets, updateTicketStatus, updateTicketPriority, addTicketMessage
} from '../../services/supportTicketService';

const PRIORITY_STYLES = {
  high: { bg: '#FEE2E2', text: '#DC2626', border: '#FCA5A5', label: 'High' },
  medium: { bg: '#FEF9C3', text: '#A16207', border: '#FDE047', label: 'Medium' },
  low: { bg: '#DCFCE7', text: '#16A34A', border: '#86EFAC', label: 'Low' },
};

const STATUS_STYLES = {
  open: { bg: '#DBEAFE', text: '#2563EB', label: 'Open' },
  'in-progress': { bg: '#FEF9C3', text: '#A16207', label: 'In Progress' },
  resolved: { bg: '#DCFCE7', text: '#16A34A', label: 'Resolved' },
};

const SupportHub = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const all = await getAllTickets();
      setTickets(all);
    } catch (error) {
      console.error('Error loading tickets:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const sortByVIP = (a, b) => (b.isVIP ? 1 : 0) - (a.isVIP ? 1 : 0);
  const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in-progress').sort(sortByVIP);
  const resolvedTickets = tickets.filter(t => t.status === 'resolved');
  const displayTickets = showArchive ? resolvedTickets : openTickets;

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateTicketStatus(ticketId, newStatus);
      setTickets(prev =>
        prev.map(t => t.id === ticketId
          ? { ...t, status: newStatus, updatedAt: new Date(), ...(newStatus === 'resolved' ? { resolvedAt: new Date() } : {}) }
          : t
        )
      );
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({
          ...prev,
          status: newStatus,
          ...(newStatus === 'resolved' ? { resolvedAt: new Date() } : {}),
        }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      await updateTicketPriority(ticketId, newPriority);
      setTickets(prev =>
        prev.map(t => t.id === ticketId ? { ...t, priority: newPriority } : t)
      );
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const message = {
        from: 'superadmin',
        fromName: 'Support Team',
        text: replyText.trim(),
      };
      await addTicketMessage(selectedTicket.id, message);

      const newMessage = { ...message, timestamp: new Date().toISOString() };
      setTickets(prev =>
        prev.map(t =>
          t.id === selectedTicket.id
            ? { ...t, messages: [...(t.messages || []), newMessage] }
            : t
        )
      );
      setSelectedTicket(prev => ({
        ...prev,
        messages: [...(prev.messages || []), newMessage],
      }));
      setReplyText('');

      if (selectedTicket.status === 'open') {
        await handleStatusChange(selectedTicket.id, 'in-progress');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    }
    setSending(false);
  };

  const formatDate = (val) => {
    if (!val) return '—';
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#516469' }} />
        <span className="ml-2" style={{ color: '#516469' }}>Loading tickets...</span>
      </div>
    );
  }

  // ---- TICKET DETAIL VIEW ----
  if (selectedTicket) {
    const pStyle = PRIORITY_STYLES[selectedTicket.priority] || PRIORITY_STYLES.medium;
    const sStyle = STATUS_STYLES[selectedTicket.status] || STATUS_STYLES.open;

    return (
      <div>
        <button
          onClick={() => { setSelectedTicket(null); setReplyText(''); }}
          className="flex items-center gap-2 mb-4 text-sm font-semibold"
          style={{ color: '#516469' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to tickets
        </button>

        <div className="bg-white rounded-lg shadow p-5 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#071D39' }}>{selectedTicket.subject}</h2>
              <p className="text-sm mt-1" style={{ color: '#516469' }}>
                From <span className="font-semibold">{selectedTicket.submittedByName}</span> · {formatDate(selectedTicket.createdAt)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="px-2 py-1 rounded text-xs font-semibold"
                style={{ backgroundColor: pStyle.bg, color: pStyle.text, border: `1px solid ${pStyle.border}` }}
              >
                {pStyle.label}
              </span>
              <span
                className="px-2 py-1 rounded text-xs font-semibold"
                style={{ backgroundColor: sStyle.bg, color: sStyle.text }}
              >
                {sStyle.label}
              </span>
            </div>
          </div>

          <div className="p-3 rounded mb-4" style={{ backgroundColor: '#F7FAFC', color: '#071D39' }}>
            {selectedTicket.description}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold" style={{ color: '#516469' }}>Status:</span>
              <select
                value={selectedTicket.status}
                onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                className="text-xs p-1 rounded border"
                style={{ borderColor: '#89A8B1', color: '#071D39' }}
              >
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold" style={{ color: '#516469' }}>Priority:</span>
              <select
                value={selectedTicket.priority}
                onChange={(e) => handlePriorityChange(selectedTicket.id, e.target.value)}
                className="text-xs p-1 rounded border"
                style={{ borderColor: '#89A8B1', color: '#071D39' }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            {selectedTicket.status !== 'resolved' && (
              <button
                onClick={() => handleStatusChange(selectedTicket.id, 'resolved')}
                className="flex items-center gap-1 px-3 py-1 rounded text-xs font-semibold text-white"
                style={{ backgroundColor: '#75BB2E' }}
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Resolve
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ borderColor: '#E2E8F0' }}>
            <h3 className="font-semibold text-sm" style={{ color: '#071D39' }}>
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Conversation ({(selectedTicket.messages || []).length} messages)
            </h3>
          </div>

          <div className="p-4 space-y-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {(!selectedTicket.messages || selectedTicket.messages.length === 0) ? (
              <p className="text-sm text-center py-6" style={{ color: '#89A8B1' }}>No replies yet. Send the first response below.</p>
            ) : (
              selectedTicket.messages.map((msg, idx) => {
                const isSA = msg.from === 'superadmin';
                return (
                  <div
                    key={idx}
                    className="p-3 rounded"
                    style={{
                      backgroundColor: isSA ? '#071D39' : '#F0F4F5',
                      marginLeft: isSA ? '40px' : '0',
                      marginRight: isSA ? '0' : '40px',
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold" style={{ color: isSA ? '#89A8B1' : '#516469' }}>
                        {msg.fromName || (isSA ? 'Support Team' : 'Client')}
                      </span>
                      <span className="text-xs" style={{ color: '#89A8B1' }}>
                        {formatDate(msg.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: isSA ? '#FFFFFF' : '#071D39' }}>
                      {msg.text}
                    </p>
                  </div>
                );
              })
            )}
          </div>

          {selectedTicket.status !== 'resolved' && (
            <div className="p-4 border-t" style={{ borderColor: '#E2E8F0' }}>
              <div className="flex gap-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  rows={2}
                  className="flex-1 p-2 rounded border text-sm"
                  style={{ borderColor: '#89A8B1', color: '#071D39' }}
                />
                <button
                  onClick={handleSendReply}
                  disabled={sending || !replyText.trim()}
                  className="px-4 py-2 rounded text-sm font-semibold text-white flex items-center gap-1 self-end"
                  style={{
                    backgroundColor: sending || !replyText.trim() ? '#89A8B1' : '#071D39',
                    cursor: sending || !replyText.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Send className="w-3.5 h-3.5" />
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          )}

          {selectedTicket.status === 'resolved' && (
            <div className="p-4 border-t text-center" style={{ borderColor: '#E2E8F0' }}>
              <p className="text-sm" style={{ color: '#89A8B1' }}>
                This ticket is resolved. Change the status above to reopen it and reply.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- TICKET LIST VIEW ----
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#071D39' }}>
            {showArchive ? 'Resolved Tickets' : 'Support Hub'}
          </h2>
          <p className="text-sm mt-1" style={{ color: '#516469' }}>
            {showArchive
              ? `${resolvedTickets.length} resolved ticket${resolvedTickets.length !== 1 ? 's' : ''}`
              : `${openTickets.length} open ticket${openTickets.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowArchive(!showArchive)}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-semibold border"
            style={{
              borderColor: showArchive ? '#071D39' : '#89A8B1',
              color: showArchive ? '#071D39' : '#516469',
              backgroundColor: showArchive ? '#F0F4F5' : 'transparent',
            }}
          >
            {showArchive ? <Inbox className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            {showArchive ? 'Back to Open' : 'View Resolved'}
          </button>
          <button
            onClick={loadTickets}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-semibold"
            style={{ backgroundColor: '#071D39', color: '#FFFFFF' }}
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      {displayTickets.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          {showArchive ? (
            <>
              <Archive className="w-12 h-12 mx-auto mb-4" style={{ color: '#89A8B1' }} />
              <p style={{ color: '#516469' }}>No resolved tickets yet.</p>
            </>
          ) : (
            <>
              <HeadphonesIcon className="w-12 h-12 mx-auto mb-4" style={{ color: '#89A8B1' }} />
              <p className="font-semibold" style={{ color: '#071D39' }}>All clear!</p>
              <p className="text-sm mt-1" style={{ color: '#516469' }}>No open tickets right now.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {displayTickets.map((ticket) => {
            const pStyle = PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium;
            const sStyle = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
            const messageCount = (ticket.messages || []).length;

            return (
              <div
                key={ticket.id}
                className="rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedTicket(ticket)}
                style={{ borderLeft: `4px solid ${pStyle.text}`, backgroundColor: '#EDF2F7' }}
              >
                <div className="px-4 py-3 flex items-center gap-3">
                  <span
                    className="px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: pStyle.bg, color: pStyle.text, border: `1px solid ${pStyle.border}`, minWidth: '52px', textAlign: 'center' }}
                  >
                    {pStyle.label}
                  </span>

                  {ticket.isVIP && (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0"
                      style={{ backgroundColor: '#D4AF3720', color: '#D4AF37', border: '1px solid #D4AF3750' }}
                    >
                      ★ VIP
                    </span>
                  )}

                  <span className="font-semibold text-sm truncate" style={{ color: '#071D39', minWidth: '120px', maxWidth: '250px' }}>
                    {ticket.subject}
                  </span>

                  <span
                    className="px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: sStyle.bg, color: sStyle.text }}
                  >
                    {sStyle.label}
                  </span>

                  <span className="text-xs flex-shrink-0" style={{ color: '#516469' }}>
                    {ticket.submittedByName}
                  </span>

                  <span className="text-xs flex-shrink-0" style={{ color: '#89A8B1' }}>
                    {formatDate(ticket.createdAt)}
                  </span>

                  {messageCount > 0 && (
                    <span className="text-xs flex items-center gap-1 flex-shrink-0" style={{ color: '#89A8B1' }}>
                      <MessageSquare className="w-3 h-3" />{messageCount}
                    </span>
                  )}

                  <div className="flex-1" />

                  {ticket.status !== 'resolved' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(ticket.id, 'resolved');
                      }}
                      className="flex items-center gap-1 px-3 py-1 rounded text-xs font-semibold text-white flex-shrink-0"
                      style={{ backgroundColor: '#75BB2E' }}
                      title="Resolve this ticket"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupportHub;